import React, { useEffect, useRef, useState } from "react";
import {
  View,
  Text,
  Image,
  Pressable,
  Modal,
  Platform,
  ActivityIndicator,
} from "react-native";
import { white } from "../assets/style/colors";
import { fixRemoteUrl } from "../../imsdk/api";

export type PreviewItem = {
  mid: string;
  type: string;
  content: string;
  thumbnailPath?: string;
  upload_path?: string;
  path?: string;
};

export interface ImagePreviewModalProps {
  visible: boolean;
  items: PreviewItem[];
  initialIndex?: number;
  onClose: () => void;
}

export const ImagePreviewModal = ({
  visible,
  items,
  initialIndex = 0,
  onClose,
}: ImagePreviewModalProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  /** -------- video state (web) -------- */
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [paused, setPaused] = useState(true);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex, items]);

  useEffect(() => {
    setPaused(true);
    setCurrentTime(0);
    setDuration(0);
    setIsVideoLoaded(false);

    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [currentIndex]);

  // 在 web 平台注入 range 滑块样式，确保轨道可见
  useEffect(() => {
    if (Platform.OS !== "web") return;
    const styleId = 'bee-range-styles';
    if (document.getElementById(styleId)) return;
    const css = `
      .bee-range { -webkit-appearance: none; appearance: none; width: 100%; height: 8px; background: rgba(255,255,255,0.18); border-radius: 4px; outline: none; }
      .bee-range::-webkit-slider-runnable-track { height: 8px; background: rgba(255,255,255,0.18); border-radius: 4px; }
      .bee-range::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 16px; height: 16px; margin-top: -4px; border-radius: 50%; background: #1E90FF; box-shadow: 0 0 0 6px rgba(30,144,255,0.12); cursor: pointer; }
      .bee-range::-moz-range-track { height: 8px; background: rgba(255,255,255,0.18); border-radius: 4px; }
      .bee-range::-moz-range-thumb { width: 16px; height: 16px; border-radius: 50%; background: #1E90FF; border: none; cursor: pointer; }
      .bee-range::-ms-track { height: 8px; background: transparent; border-color: transparent; color: transparent; }
    `;
    const styleEl = document.createElement('style');
    styleEl.id = styleId;
    styleEl.appendChild(document.createTextNode(css));
    document.head.appendChild(styleEl);
    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, []);

  if (!visible || items.length === 0) return null;

  const currentItem = items[currentIndex];
  const isVideo = currentItem.type === "video";

  // 清理 URL 中的额外字符（如反引号和空格）
  const cleanUrl = (url: string) => {
    if (!url) return "";
    // 移除反引号、空格和其他可能的额外字符
    let cleaned = url.replace(/[`'"\s]/g, "").trim();
    // 再次检查并移除可能的反引号，确保完全清理
    cleaned = cleaned.replace(/[`]/g, "");
    return cleaned;
  };


  const getContentUrl = (item: PreviewItem) => {
    return item.type === "video" || item.type === "image" || item.type === 'voice' ? fixRemoteUrl(item.content) : item.content;
  }

  const mediaUrl = getContentUrl(currentItem) || "";

  /* ================= 下载功能（修复文件类型处理） ================= */
  const downloadFile = async (item: PreviewItem) => {
    // 只处理 Web 平台
    if (Platform.OS !== "web") return;

    try {
      let downloadUrl = cleanUrl(item.content || item.path || item.upload_path || "");
      // downloadUrl = downloadUrl.replace('https://bmessenger.s3.ap-east-1.amazonaws.com/', '/media/');
      if (!downloadUrl) return;
      // 确保 URL 是完整的
      // if (!/^https?:\/\//i.test(downloadUrl)) {
      //   console.error("Invalid URL format:", downloadUrl);
      //   return;
      // }

      // 生成文件名
      let fileName = `bee_${Date.now()}`;
      let ext = "bin";

      // 强制处理：根据 item.type 强制设置扩展名
      if (item.type === "video") {
        ext = "mp4";
      } else if (item.type === "image" || item.type === "img") {
        // 对于图片，尝试从 URL 中获取更具体的扩展名
        const urlExtMatch = downloadUrl.match(/\.([a-zA-Z0-9]{2,5})(?:\?|#|$)/);
        if (urlExtMatch && urlExtMatch[1]) {
          const urlExt = urlExtMatch[1].toLowerCase();
          const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "tiff", "svg"];
          if (imageExtensions.includes(urlExt)) {
            ext = urlExt;
          } else {
            ext = "jpg";
          }
        } else {
          ext = "jpg";
        }
      }


      // 强制下载逻辑，使用 Fetch API 将文件转换为 blob
      try {

        // 尝试使用 CORS 模式
        const response = await fetch(downloadUrl, {
          mode: 'cors',
          credentials: 'omit',
          headers: {
            'Accept': 'image/*',
            'Cache-Control': 'no-cache'
          }
        });

        if (!response.ok) {
          throw new Error(`Download failed with status ${response.status}`);
        }

        const blob = await response.blob();

        // 创建 blob URL
        const blobUrl = URL.createObjectURL(blob);

        // 创建下载链接
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        link.rel = 'noopener noreferrer';
        // 移除 target 属性，避免在新标签页打开
        // link.target = '_blank';

        // 触发下载
        document.body.appendChild(link);

        // 使用鼠标事件模拟点击，可能比直接调用 click() 更可靠
        const clickEvent = new MouseEvent('click', {
          view: window,
          bubbles: true,
          cancelable: true
        });
        link.dispatchEvent(clickEvent);

        document.body.removeChild(link);

        // 清理 blob URL
        setTimeout(() => {
          URL.revokeObjectURL(blobUrl);
        }, 1000);

      } catch (corsError) {

        // 降级方案：使用 no-cors 模式
        try {
          const response = await fetch(downloadUrl, {
            mode: 'no-cors',
            credentials: 'omit'
          });

          const blob = await response.blob();

          const blobUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = fileName;
          link.rel = 'noopener noreferrer';

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          setTimeout(() => {
            URL.revokeObjectURL(blobUrl);
          }, 1000);

        } catch (error) {
          console.error("All download methods failed:", error);

          // 最后的降级方案：使用直接链接，但尝试添加更多参数
          try {
            console.log("Using final fallback: direct link with download attribute");
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = fileName;
            a.rel = 'noopener noreferrer';
            // 添加 download 属性的变体
            a.setAttribute('download', fileName);
            document.body.appendChild(a);

            // 尝试使用不同的点击方式
            if (typeof a.click === 'function') {
              a.click();
            } else {
              // 模拟点击事件
              const event = document.createEvent('MouseEvents');
              event.initMouseEvent('click', true, true, window, 1, 0, 0, 0, 0, false, false, false, false, 0, null);
              a.dispatchEvent(event);
            }

            document.body.removeChild(a);
            console.log("Final fallback download initiated:", fileName);
          } catch (finalError) {
            console.error("Final fallback also failed:", finalError);
          }
        }
      }

      console.log("Download initiated successfully:", fileName);
    } catch (e) {
      console.error("[download error]", e);
      // 可以在这里添加用户提示
    }
  };

  const togglePlay = () => {
    if (!videoRef.current) return;

    if (paused) {
      videoRef.current.play();
    } else {
      videoRef.current.pause();
    }
    setPaused(!paused);
  };

  const handleLoadedMetadata = () => {
    if (!videoRef.current) return;

    setDuration(videoRef.current.duration || 0);

    // 🔥 强制解码首帧，避免黑屏
    videoRef.current.currentTime = 0.01;
    setIsVideoLoaded(true);
  };

  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    setCurrentTime(videoRef.current.currentTime);
  };

  const handleSeek = (value: number) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = value;
    setCurrentTime(value);
  };

  const formatTime = (t: number) => {
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <Modal visible={visible} transparent animationType="fade">
      <View
        style={{
          flex: 1,
          backgroundColor: "rgba(0,0,0,0.85)",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {/* 关闭 */}
        {/* 关闭按钮 */}
        <Pressable
          style={{
            position: "absolute",
            top: Platform.OS === "web" ? 20 : 40,
            right: Platform.OS === "web" ? 20 : 40,
            width: 40,
            height: 40,
            borderRadius: 20,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
          onPress={onClose}
        >
          <Text style={{ color: white, fontSize: 24, fontWeight: "bold" }}>
            ×
          </Text>
        </Pressable>

        {/* 下载 */}
        <Pressable
          style={{
            position: "absolute",
            top: Platform.OS === "web" ? 20 : 40,
            right: Platform.OS === "web" ? 70 : 90,
            width: 40,
            height: 40,
            borderRadius: 20,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
          onPress={() => downloadFile(currentItem)}
        >
          <Text style={{ color: white, fontSize: 18 }}>↓</Text>
        </Pressable>

        {/* 内容 */}
        <View
          style={{
            width: "90%",
            maxWidth: 900,
            height: "80%",
          }}
        >
          {isVideo && Platform.OS === "web" ? (
            <View style={{ width: "100%", height: "100%" }}>
              <video
                ref={videoRef}
                src={mediaUrl}
                preload="metadata"
                playsInline
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  backgroundColor: "#000",
                }}
                onLoadedMetadata={handleLoadedMetadata}
                onTimeUpdate={handleTimeUpdate}
                onEnded={() => setPaused(true)}
              />

              {!isVideoLoaded && (
                <ActivityIndicator
                  size="large"
                  color="#fff"
                  style={{ position: "absolute", top: "45%", left: "50%" }}
                />
              )}

              {/* 控制栏 */}
              <View
                style={{
                  position: "absolute",
                  bottom: 20,
                  left: 20,
                  right: 20,
                  backgroundColor: "rgba(0,0,0,0.6)",
                  padding: 10,
                  borderRadius: 8,
                }}
              >
                <input
                  type="range"
                  min={0}
                  max={duration}
                  step={0.1}
                  className="bee-range"
                  value={currentTime}
                  onChange={(e) => handleSeek(Number(e.target.value))}
                  style={{ width: "100%" }}
                />

                <View
                  style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    marginTop: 6,
                  }}
                >
                  <Pressable onPress={togglePlay}>
                    <Text style={{ color: "#fff" }}>
                      {paused ? "▶ 播放" : "⏸ 暂停"}
                    </Text>
                  </Pressable>

                  <Text style={{ color: "#fff", fontSize: 12 }}>
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </Text>
                </View>
              </View>
            </View>
          ) : (
            <Image
              source={{ uri: mediaUrl }}
              resizeMode="contain"
              style={{ width: "100%", height: "100%" }}
            />
          )}
        </View>

        {/* 翻页 */}
        {items.length > 1 && (
          <>
            {currentIndex > 0 && (
              <Pressable
                onPress={() => setCurrentIndex((v) => v - 1)}
                style={{ position: "absolute", left: 20 }}
              >
                <Text style={{ color: "#fff", fontSize: 30 }}>‹</Text>
              </Pressable>
            )}
            {currentIndex < items.length - 1 && (
              <Pressable
                onPress={() => setCurrentIndex((v) => v + 1)}
                style={{ position: "absolute", right: 20 }}
              >
                <Text style={{ color: "#fff", fontSize: 30 }}>›</Text>
              </Pressable>
            )}
          </>
        )}
      </View>
    </Modal>
  );
};
